import Search from '@mui/icons-material/Search';
import { inputBaseClasses } from '@mui/material';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import OutlinedInput, { outlinedInputClasses } from '@mui/material/OutlinedInput';
import { t } from 'i18next';
import { debounce } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { GLOBAL_DEBOUNCE_DEBOUNCE_DELAY_IN_MS } from '~constants/consts';
import { NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX } from '~constants/filters';

const DEFAULT_SEARCH_MIN_LENGTH = 3;

type Props = {
  initialValue?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  searchMinLength?: number;
};

export default function DebouncedSearchBox({
  initialValue = '',
  onChange,
  placeholder,
  searchMinLength = DEFAULT_SEARCH_MIN_LENGTH,
}: Props) {
  const [value, setValue] = useState(initialValue);
  const lastSearch = useRef(initialValue);
  const isInitialMount = useRef(true);

  // Keep a ref to the latest `onChange` so the debounced callback below (created
  // once) always invokes the current handler. Without this, the debounced call
  // captures the handler from the first render, which closes over stale
  // `useSearchParams` state and can clobber query params that changed after
  // mount (e.g. resetting the Driver Pay category tab on search). See TRE-15462.
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const shouldSearch = useMemo(() => {
    const effectiveValue = value.trim();
    const searchDidChange = lastSearch.current !== effectiveValue;

    if (searchDidChange) {
      return effectiveValue.length === 0 || effectiveValue.length >= searchMinLength;
    }

    return false;
  }, [searchMinLength, value]);

  const hasValue = value.length > 0;

  const debouncedOnChange = useRef(
    debounce((value: string) => {
      lastSearch.current = value;
      onChangeRef.current(value);
    }, GLOBAL_DEBOUNCE_DEBOUNCE_DELAY_IN_MS),
  );

  const handleInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    setValue(value);
  }, []);

  const handleClearInput = useCallback(() => {
    setValue('');
  }, []);

  useEffect(() => {
    const debounced = debouncedOnChange.current;

    if (shouldSearch) {
      debounced(value.trim());
    }

    return () => {
      debounced.cancel();
    };
  }, [shouldSearch, value]);

  // Trigger search immediately on mount if there's an initial value
  useEffect(() => {
    if (isInitialMount.current && initialValue) {
      isInitialMount.current = false;
      onChange(initialValue.trim());
    }
  }, [initialValue]);

  return (
    <OutlinedInput
      fullWidth
      onChange={handleInputChange}
      placeholder={placeholder}
      size="small"
      value={value}
      startAdornment={
        <InputAdornment position="start">
          <Search
            sx={(theme) => ({
              color: theme.brandV2.colors.treadBlack,
              cursor: 'pointer',
              fontSize: '16px',
            })}
          />
        </InputAdornment>
      }
      endAdornment={
        <InputAdornment position="end" onClick={handleClearInput}>
          <Button
            color="brandV2Orange"
            size="small"
            variant="text"
            sx={(theme) => ({
              border: 'solid 1px transparent',
              fontSize: '12px',
              height: '20px',
              lineHeight: '20px',
              minWidth: 'auto',
              px: '6px',
              visibility: hasValue ? 'visible' : 'hidden',
              '&:hover': { borderColor: theme.brandV2.colors.treadOrange },
            })}
          >
            {t('common.clear')}
          </Button>
        </InputAdornment>
      }
      sx={(theme) => ({
        [`&.${inputBaseClasses.root}`]: {
          height: `calc(${NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX}px - 2px)`,
          px: 1,
          py: 0.5,
          [`.${inputBaseClasses.input}`]: { fontSize: '12px', p: 0 },
          [`.${outlinedInputClasses.notchedOutline}`]: {
            border: `solid 1px ${theme.brandV2.colors.treadGray7}`,
          },
          [`&.${inputBaseClasses.focused} .${outlinedInputClasses.notchedOutline}`]: {
            border: `solid 1px ${theme.brandV2.colors.treadGray7}`,
          },
        },
      })}
    />
  );
}
